import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CrisisResources } from "@/components/CrisisResources";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { ReportButton } from "@/components/ReportButton";
import { toast } from "sonner";
import { Users, Heart, MessageCircle, ShieldAlert, EyeOff, Send, Pencil, Trash2, Check, X } from "lucide-react";

export default function Community() {
  const { t } = useI18n();
  const { user } = useAuth();
  const myId = user?.user_id;
  const [params, setParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [anon, setAnon] = useState(true);
  const [posting, setPosting] = useState(false);
  const [warning, setWarning] = useState(null);
  const [crisis, setCrisis] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [openComments, setOpenComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [commenting, setCommenting] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostText, setEditPostText] = useState("");
  const [editingComment, setEditingComment] = useState(null); // { postId, commentId }
  const [editCommentText, setEditCommentText] = useState("");

  useEffect(() => { api.get("/community/groups").then(({ data }) => setGroups(data.groups || [])).catch(() => {}); }, []);
  const loadPosts = (g) => api.get("/community/posts", { params: g ? { group: g } : {} }).then(({ data }) => setPosts(data.posts || [])).catch(() => {});
  useEffect(() => { loadPosts(active); }, [active]);

  // Deep-link from a notification: open that post's replies and scroll to it.
  const focusPost = params.get("post");
  useEffect(() => {
    if (!focusPost || posts.length === 0) return;
    if (!posts.some((p) => p.post_id === focusPost)) return;
    setOpenComments((o) => ({ ...o, [focusPost]: true }));
    const el = document.getElementById(`post-${focusPost}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setParams({}, { replace: true });
  }, [focusPost, posts, setParams]);

  const submit = async (override = false) => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/community/posts", { content, group: active || "growth", anonymous: anon, override_warning: override });
      if (data.warning) { setWarning(data); setPosting(false); return; }
      if (data.blocked) {
        setWarning(null);
        toast.error(data.message);
        if (data.show_crisis_resources) setCrisis(true);
        setContent("");
        setPosting(false);
        return;
      }
      setContent(""); setWarning(null); loadPosts(active);
      toast.success("Shared with the community 🌿");
    } catch (e) { toast.error(apiErr(e)); } finally { setPosting(false); }
  };

  const like = async (id) => { const { data } = await api.post(`/community/posts/${id}/like`); setPosts((p) => p.map((x) => x.post_id === id ? { ...x, liked: data.liked, like_count: data.like_count } : x)); };

  const toggleComments = (id) => setOpenComments((o) => ({ ...o, [id]: !o[id] }));

  const submitComment = async (id) => {
    const text = (commentText[id] || "").trim();
    if (!text) return;
    setCommenting(id);
    try {
      const { data } = await api.post(`/community/posts/${id}/comment`, { content: text, anonymous: anon });
      if (data.blocked) { toast.error(data.message); return; }
      setPosts((p) => p.map((x) => x.post_id === id ? { ...x, comments: [...(x.comments || []), data.comment] } : x));
      setCommentText((c) => ({ ...c, [id]: "" }));
      toast.success("Reply shared 🌿");
    } catch (e) { toast.error(apiErr(e)); } finally { setCommenting(null); }
  };

  // --- Author edit / delete ---
  const saveEditPost = async (id) => {
    const text = editPostText.trim();
    if (!text) return;
    try {
      const { data } = await api.patch(`/community/posts/${id}`, { content: text });
      if (data.blocked) { toast.error(data.message); return; }
      setPosts((p) => p.map((x) => x.post_id === id ? { ...x, content: data.content, edited_at: new Date().toISOString() } : x));
      setEditingPost(null);
      toast.success(t("comm.postUpdated"));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deletePost = (id) => {
    toast(t("comm.deletePostQ"), {
      action: {
        label: t("comm.delete"),
        onClick: async () => {
          try {
            await api.delete(`/community/posts/${id}`);
            setPosts((p) => p.filter((x) => x.post_id !== id));
            toast.success(t("comm.postDeleted"));
          } catch (e) { toast.error(apiErr(e)); }
        },
      },
    });
  };
  const saveEditComment = async (postId, commentId) => {
    const text = editCommentText.trim();
    if (!text) return;
    try {
      const { data } = await api.patch(`/community/posts/${postId}/comments/${commentId}`, { content: text });
      if (data.blocked) { toast.error(data.message); return; }
      setPosts((p) => p.map((x) => x.post_id === postId
        ? { ...x, comments: (x.comments || []).map((c) => c.comment_id === commentId ? { ...c, content: data.content, edited_at: new Date().toISOString() } : c) }
        : x));
      setEditingComment(null);
      toast.success(t("comm.commentUpdated"));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deleteComment = (postId, commentId) => {
    toast(t("comm.deleteCommentQ"), {
      action: {
        label: t("comm.delete"),
        onClick: async () => {
          try {
            await api.delete(`/community/posts/${postId}/comments/${commentId}`);
            setPosts((p) => p.map((x) => x.post_id === postId
              ? { ...x, comments: (x.comments || []).filter((c) => c.comment_id !== commentId) } : x));
            toast.success(t("comm.commentDeleted"));
          } catch (e) { toast.error(apiErr(e)); }
        },
      },
    });
  };

  return (
    <div>
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("community.title")}</h1>
      <p className="mt-2 max-w-lg text-muted-foreground">{t("community.subtitle")}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => setActive(null)} data-testid="group-all"
                className={`rounded-full px-4 py-1.5 text-sm transition-colors duration-300 ${!active ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>{t("community.allSpaces")}</button>
        {groups.map((g) => (
          <button key={g.id} onClick={() => setActive(g.id)} data-testid={`group-${g.id}`}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors duration-300 ${active === g.id ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>{g.name}</button>
        ))}
      </div>

      <div className="mt-6 rounded-3xl border border-border/60 bg-card p-5 ambient-shadow">
        <Textarea value={content} onChange={(e) => { setContent(e.target.value); setWarning(null); }} placeholder={t("community.postPh")}
                  data-testid="community-post-input" className="min-h-24 resize-none rounded-2xl border-0 bg-transparent focus-visible:ring-0 shadow-none" />
        {warning && (
          <div className="mb-3 flex items-start gap-2 rounded-2xl bg-amber-500/10 p-3 text-sm">
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" strokeWidth={1.5} />
            <div>
              <p className="font-medium text-amber-700 dark:text-amber-400">{warning.message}</p>
              {warning.moderation?.suggestion && <p className="mt-1 text-muted-foreground">{warning.moderation.suggestion}</p>}
              <Button size="sm" variant="ghost" className="mt-2 rounded-full text-amber-700" onClick={() => submit(true)} data-testid="post-anyway-button">{t("community.postAnyway")}</Button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={anon} onCheckedChange={setAnon} data-testid="anon-toggle" /> {t("community.anon")}
          </label>
          <Button onClick={() => submit(false)} disabled={posting || !content.trim()} className="rounded-full gap-2" data-testid="community-post-button">
            <Send className="h-4 w-4" strokeWidth={1.5} /> {posting ? t("community.posting") : t("community.share")}
          </Button>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {posts.length === 0 && <p className="text-sm text-muted-foreground">{t("community.empty")}</p>}
        {posts.map((p, i) => {
          const hidden = p.status === "hidden" && !revealed[p.post_id];
          return (
            <motion.div key={p.post_id} id={`post-${p.post_id}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.05 }}
                        className={`ambient-shadow rounded-3xl border bg-card p-6 transition-colors ${focusPost === p.post_id ? "border-primary/60" : "border-border/60"}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-secondary text-xs font-medium">
                    <Avatar picture={p.author_picture} name={p.author_name} />
                  </div>
                  <span className="text-sm font-medium">{p.author_name}</span>
                  <span className="text-xs text-muted-foreground">· {new Date(p.created_at).toLocaleDateString()}{p.edited_at ? ` · ${t("comm.edited")}` : ""}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="rounded-full text-xs">{groups.find((g) => g.id === p.group)?.name || p.group}</Badge>
                  {myId && p.user_id === myId && editingPost !== p.post_id && (
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => { setEditingPost(p.post_id); setEditPostText(p.content); }}
                              data-testid={`edit-post-${p.post_id}`} title={t("comm.edit")}
                              className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground">
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                      <button onClick={() => deletePost(p.post_id)} data-testid={`delete-post-${p.post_id}`} title={t("comm.delete")}
                              className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  )}
                  {myId && p.user_id !== myId && (
                    <ReportButton postId={p.post_id} />
                  )}
                </div>
              </div>
              {editingPost === p.post_id ? (
                <div className="mt-3" data-testid={`edit-post-form-${p.post_id}`}>
                  <Textarea value={editPostText} onChange={(e) => setEditPostText(e.target.value)}
                            className="min-h-20 resize-none rounded-2xl text-sm" data-testid={`edit-post-input-${p.post_id}`} />
                  <div className="mt-2 flex justify-end gap-2">
                    <Button size="sm" variant="ghost" className="rounded-full gap-1" onClick={() => setEditingPost(null)} data-testid={`edit-post-cancel-${p.post_id}`}>
                      <X className="h-4 w-4" strokeWidth={1.5} /> {t("comm.cancel")}
                    </Button>
                    <Button size="sm" className="rounded-full gap-1" onClick={() => saveEditPost(p.post_id)} disabled={!editPostText.trim()} data-testid={`edit-post-save-${p.post_id}`}>
                      <Check className="h-4 w-4" strokeWidth={1.5} /> {t("comm.save")}
                    </Button>
                  </div>
                </div>
              ) : hidden ? (
                <div className="mt-4 rounded-2xl bg-muted/60 p-5 text-center">
                  <EyeOff className="mx-auto h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                  <p className="mt-2 text-sm text-muted-foreground">This content was hidden by AI moderation ({p.moderation_reason || "possibly harmful"}). Nothing is silently deleted.</p>
                  <Button size="sm" variant="ghost" className="mt-2 rounded-full" onClick={() => setRevealed((r) => ({ ...r, [p.post_id]: true }))} data-testid={`reveal-${p.post_id}`}>{t("community.viewAnyway")}</Button>
                </div>
              ) : (
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{p.content}</p>
              )}
              <div className="mt-4 flex items-center gap-4">
                <button onClick={() => like(p.post_id)} data-testid={`like-${p.post_id}`}
                        className={`flex items-center gap-1.5 text-sm transition-colors duration-300 ${p.liked ? "text-destructive" : "text-muted-foreground hover:text-destructive"}`}>
                  <Heart className={`h-4 w-4 ${p.liked ? "fill-current" : ""}`} strokeWidth={1.5} /> {p.like_count || 0}
                </button>
                <button onClick={() => toggleComments(p.post_id)} data-testid={`comment-toggle-${p.post_id}`}
                        className={`flex items-center gap-1.5 text-sm transition-colors duration-300 ${openComments[p.post_id] ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                  <MessageCircle className="h-4 w-4" strokeWidth={1.5} /> {(p.comments || []).length}
                </button>
              </div>

              {openComments[p.post_id] && !hidden && (
                <div className="mt-4 space-y-3 border-t border-border/50 pt-4" data-testid={`comments-${p.post_id}`}>
                  {(p.comments || []).map((c) => (
                    <div key={c.comment_id} className="group flex items-start gap-2" data-testid={`comment-${c.comment_id}`}>
                      <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary text-xs font-medium">
                        <Avatar picture={c.author_picture} name={c.author_name} />
                      </div>
                      {editingComment && editingComment.commentId === c.comment_id ? (
                        <div className="flex-1" data-testid={`edit-comment-form-${c.comment_id}`}>
                          <Textarea value={editCommentText} onChange={(e) => setEditCommentText(e.target.value)}
                                    className="min-h-10 resize-none rounded-2xl text-sm" data-testid={`edit-comment-input-${c.comment_id}`} />
                          <div className="mt-1.5 flex justify-end gap-2">
                            <Button size="sm" variant="ghost" className="h-7 rounded-full gap-1 text-xs" onClick={() => setEditingComment(null)} data-testid={`edit-comment-cancel-${c.comment_id}`}>
                              <X className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("comm.cancel")}
                            </Button>
                            <Button size="sm" className="h-7 rounded-full gap-1 text-xs" onClick={() => saveEditComment(p.post_id, c.comment_id)} disabled={!editCommentText.trim()} data-testid={`edit-comment-save-${c.comment_id}`}>
                              <Check className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("comm.save")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="rounded-2xl bg-secondary/40 px-3 py-2">
                            <p className="text-xs font-medium">{c.author_name} <span className="font-normal text-muted-foreground">· {new Date(c.created_at).toLocaleDateString()}{c.edited_at ? ` · ${t("comm.edited")}` : ""}</span></p>
                            <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed">{c.content}</p>
                          </div>
                          {myId && c.user_id === myId && (
                            <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                              <button onClick={() => { setEditingComment({ postId: p.post_id, commentId: c.comment_id }); setEditCommentText(c.content); }}
                                      data-testid={`edit-comment-${c.comment_id}`} title={t("comm.edit")}
                                      className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-accent/60 hover:text-foreground">
                                <Pencil className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                              <button onClick={() => deleteComment(p.post_id, c.comment_id)} data-testid={`delete-comment-${c.comment_id}`} title={t("comm.delete")}
                                      className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                                <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                            </div>
                          )}
                          {myId && c.user_id !== myId && (
                            <div className="opacity-0 transition-opacity group-hover:opacity-100">
                              <ReportButton postId={p.post_id} commentId={c.comment_id} size="xs" />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                  {(p.comments || []).length === 0 && <p className="text-xs text-muted-foreground">Be the first to reply with kindness.</p>}
                  <div className="flex items-end gap-2">
                    <Textarea value={commentText[p.post_id] || ""} onChange={(e) => setCommentText((c) => ({ ...c, [p.post_id]: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitComment(p.post_id); } }}
                              placeholder="Write a supportive reply…" data-testid={`comment-input-${p.post_id}`}
                              className="min-h-10 max-h-32 resize-none rounded-2xl text-sm" />
                    <Button size="icon" className="h-10 w-10 flex-shrink-0 rounded-full" onClick={() => submitComment(p.post_id)}
                            disabled={commenting === p.post_id || !(commentText[p.post_id] || "").trim()} data-testid={`comment-submit-${p.post_id}`}>
                      <Send className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
      <CrisisResources open={crisis} onClose={() => setCrisis(false)} />
    </div>
  );
}
